/**
 * ReportCard Component
 *
 * Displays a single report option with title and description.
 * Shows disabled state for reports not available at the current location.
 */

import React from 'react';
import { DocumentChartBarIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';

interface ReportCardProps {
  report: ReportDefinition;
  isAvailable: boolean;
  isComingSoon: boolean;
  locationName: string;
  onClick: () => void;
}

export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  isAvailable,
  isComingSoon,
  locationName,
  onClick,
}) => {
  const handleClick = () => {
    if (isAvailable) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isAvailable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={isAvailable ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={isComingSoon ? 'Coming soon' : isAvailable ? `Run ${report.title}` : `Not available for ${locationName}`}
      className={`
        relative p-4 rounded-lg border transition-all
        ${isAvailable
          ? 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm cursor-pointer'
          : 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
        }
      `}
    >
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg flex-shrink-0 ${isAvailable ? 'bg-blue-100' : 'bg-gray-200'}`}>
          <DocumentChartBarIcon
            className={`h-5 w-5 ${isAvailable ? 'text-blue-600' : 'text-gray-400'}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
            {report.title}
            {isComingSoon && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-500">
                Coming Soon
              </span>
            )}
          </h4>
          <p className={`mt-1 text-xs ${isAvailable ? 'text-gray-500' : 'text-gray-400'}`}>
            {report.description}
          </p>
        </div>
        {isAvailable && (
          <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </div>
      {!isAvailable && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="sr-only">Not available for {locationName}</span>
        </div>
      )}
    </div>
  );
};
